import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Folder, Search } from 'lucide-react';
import { useProjects } from '../../hooks/useProjects';
import { CreateProjectModal } from '../../components/projects/CreateProjectModal';
import {
  Button,
  EmptyState,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../ui';
import './ProjectListPage.css';

function formatDate(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ProjectListPage() {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { data: projects, isLoading, error } = useProjects(orgId);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [query, setQuery] = useState('');

  const handleSelectProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCreateSuccess = (projectId: string) => {
    setShowCreateModal(false);
    navigate(`/projects/${projectId}`);
  };

  const filtered = useMemo(() => {
    if (!projects) return projects;
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      p => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
    );
  }, [projects, query]);

  if (isLoading) {
    return (
      <div className="project-list">
        <div className="project-list__loading">
          <div className="spinner" />
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-list">
        <div className="project-list__error">
          <p>Failed to load projects</p>
          <p className="project-list__error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  const hasProjects = projects && projects.length > 0;

  return (
    <div className="project-list">
      <header className="project-list__header">
        <div className="project-list__heading">
          <h1>Projects</h1>
          <p className="project-list__subtitle">
            OAuth / magic link / SCIM projects scoped to this organization.
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>+ Create Project</Button>
      </header>

      {hasProjects ? (
        <>
          <div className="project-list__toolbar">
            <Input
              inputSize="md"
              leadingAddon={<Search size={14} aria-hidden="true" />}
              placeholder="Search projects"
              value={query}
              onChange={e => setQuery(e.target.value)}
              aria-label="Search projects"
              wrapperClassName="project-list__search"
            />
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell className="project-list__date-col">Created</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered && filtered.length > 0 ? (
                filtered.map(project => (
                  <TableRow
                    key={project.id}
                    interactive
                    onClick={() => handleSelectProject(project.id)}
                  >
                    <TableCell>
                      <span className="project-list__name-cell">
                        <span className="project-list__name-icon" aria-hidden="true">
                          <Folder size={14} />
                        </span>
                        <span className="project-list__name-text">
                          <span className="project-list__name">{project.name}</span>
                          <span className="project-list__slug">{project.slug}</span>
                        </span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="project-list__description">
                        {project.description ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell className="project-list__date-col">
                      <span className="project-list__date">{formatDate(project.created_at)}</span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <div className="project-list__empty-search">
                      No projects match &ldquo;{query}&rdquo;.
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      ) : (
        <EmptyState
          icon={<Folder size={20} />}
          title="No projects yet"
          description="Create your first project to start configuring authentication."
          action={<Button onClick={() => setShowCreateModal(true)}>Create Project</Button>}
        />
      )}

      {showCreateModal && orgId && (
        <CreateProjectModal
          organizationId={orgId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
